import { PDFDocument, PDFPage, rgb, PDFFont, PDFImage } from 'pdf-lib';
import { getFont } from '../../utils';
import fontkit from '@pdf-lib/fontkit';
import { TLedger, TLedgerPictur } from '../../types/Ledger';
import { TTenant } from '../../types/common';
import { generatePdfUint8Array } from '../../generatePdf/pdfUtilities';

const lineColor = rgb(0.5, 0.5, 0.5);

let y = 548;
let point = 0;

export const LedgerPdf = async (
  data: TLedger,
  userTenantData: TTenant | undefined
) => {
  y = 548;

  const arrayBufferData = await getFont();

  const mincho = arrayBufferData.mincho;

  const pdfDoc: PDFDocument = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const font = await pdfDoc.embedFont(mincho.data, { subset: mincho.subset });

  const ledgerPictures = data.pictures;

  if (!ledgerPictures) {
    return;
  }

  const page1: any = await addTitlePage(data, arrayBufferData, userTenantData);

  const pdfDoc1 = await PDFDocument.load(page1);

  const copiedPage1 = await pdfDoc.copyPages(pdfDoc1, pdfDoc1.getPageIndices());

  copiedPage1.forEach(page => pdfDoc.addPage(page));

  let page: PDFPage = pdfDoc.addPage([595, 842]);

  await drowAlonePageBase(page);

  for (let i = 0; ledgerPictures.length > i; i++) {
    if (i != 0 && i % 3 == 0) {
      page = pdfDoc.addPage([595, 842]);
      await drowAlonePageBase(page);
      y = 548;
    }

    await drowInfo(ledgerPictures[i], page, font, pdfDoc);
  }
  const pdfBytes = await pdfDoc.save();

  const blob = new Blob([pdfBytes.buffer], { type: 'application/pdf' });
  return blob;
};

const addTitlePage = async (
  data: TLedger,
  font: any,
  userTenantData: TTenant | undefined
) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf1 = await import('../../assets/base_ledger_pdf2');

  const template: any = {
    basePdf: basePdf1.basePdf,
    schemas: [
      {
        construct_name: {
          type: 'text',
          position: {
            x: 17.51,
            y: 69.32
          },
          width: 175,
          height: 15.73,
          alignment: 'center',
          dynamicFontSize: {
            max: 38,
            min: 24
          }
        },
        name: {
          type: 'text',
          position: {
            x: 17.51,
            y: 165.9
          },
          width: 175,
          height: 13.08,
          alignment: 'center',
          fontSize: 33,
          characterSpacing: 0,
          lineHeight: 1
        },
        address: {
          type: 'text',
          position: {
            x: 17.51,
            y: 196.85
          },
          width: 175,
          height: 8.32,
          alignment: 'center',
          fontSize: 21,
          characterSpacing: 0,
          lineHeight: 1
        },
        phone_number: {
          type: 'text',
          position: {
            x: 53.64,
            y: 222.26
          },
          width: 102.73,
          height: 7.79,
          alignment: 'center',
          fontSize: 21,
          characterSpacing: 0,
          lineHeight: 1
        },
        title: {
          type: 'text',
          position: {
            x: 17.51,
            y: 105.47
          },
          width: 174.99,
          height: 58.33,
          alignment: 'center',
          dynamicFontSize: {
            max: 24,
            min: 20
          }
        }
      }
    ]
  };

  const construct_name = data.construct_name ? data.construct_name : '';
  const title = data.title ? data.title : '';
  const name = userTenantData?.name ? userTenantData.name : '';
  const address = userTenantData?.address ? userTenantData.address : '';
  const phone_number = userTenantData?.phone_number
    ? userTenantData.phone_number
    : '';

  const inputs = [
    {
      construct_name: construct_name,
      title: title,
      name: name,
      address: address,
      phone_number: phone_number
    }
  ];

  const pdf = await generatePdfUint8Array(template, inputs, font);

  return pdf;
};

const drowInfo = async (
  data: TLedgerPictur,
  page: PDFPage,
  font: PDFFont,
  pdfDoc: PDFDocument
) => {
  point = y + 211;

  // データ
  const sortOrder: string = data.sort_order ? String(data.sort_order) : '';
  const shootingDate: string = data.shooting_date ? data.shooting_date : '';
  const place: string = data.place ? data.place : '';
  const note: string = data.note ? data.note : '';
  const arrNote = note.split(/\n/);

  page.drawText(shootingDate, {
    size: 8,
    x: 360,
    y: y + 241,
    font: font
  });

  page.drawText(`No${sortOrder}`, {
    size: 8,
    x: 550,
    y: y + 241,
    font: font
  });

  await newLineTextLength(place, page, font, 360, 40);
  await drawBar(page, 358, 562);

  if (arrNote.length > 0) {
    for (let i = 0; arrNote.length > i; i++) {
      await newLineTextLength(arrNote[i], page, font, 360, 40);
    }
  } else {
    await newLineTextLength(note, page, font, 360, 40);
  }

  const imageURL = data.picture_src;

  if (!imageURL) {
    return;
  }

  try {
    const image = await convertBase64ToPDFImage(imageURL, pdfDoc);

    const imageDims = getScaledImageShape(image);

    page.drawImage(image, {
      x: 35 + (305 - imageDims.width) / 2,
      y: y + 10 + (234 - imageDims.height) / 2,
      width: imageDims.width,
      height: imageDims.height
    });

    y -= 254;
  } catch (error) {
    window.alert(error);
  }
};

const drowAlonePageBase = async (page: PDFPage) => {
  // 横線
  page.drawLine({
    start: { x: 25, y: 802 },
    end: { x: 570, y: 802 },
    thickness: 1,
    color: lineColor
  });

  page.drawLine({
    start: { x: 25, y: 548 },
    end: { x: 570, y: 548 },
    thickness: 1,
    color: lineColor
  });

  page.drawLine({
    start: { x: 25, y: 294 },
    end: { x: 570, y: 294 },
    thickness: 1,
    color: lineColor
  });

  page.drawLine({
    start: { x: 25, y: 40 },
    end: { x: 570, y: 40 },
    thickness: 1,
    color: lineColor
  });

  // 縦線
  page.drawLine({
    start: { x: 25, y: 802 },
    end: { x: 25, y: 40 },
    thickness: 1,
    color: lineColor
  });

  page.drawLine({
    start: { x: 350, y: 802 },
    end: { x: 350, y: 40 },
    thickness: 1,
    color: lineColor
  });

  page.drawLine({
    start: { x: 570, y: 802 },
    end: { x: 570, y: 40 },
    thickness: 1,
    color: lineColor
  });
};

/**
 * 20文字で改行表示を行う
 *
 * @param text 描画文字列
 * @param page object
 * @param font object
 * @param positionX x座標
 * @param wordCount 改行する文字数
 */
const newLineTextLength = async (
  text: string,
  page: PDFPage,
  font: PDFFont,
  positionX: number,
  wordCount: number
) => {
  let currentCount = 0;
  let lineText = '';

  // テキストを1文字ずつ処理
  for (const char of text) {
    // 全角文字かどうかを判定
    const isFullWidth = char.match(/[^\x01-\x7E\xA1-\xDF]/) ? true : false;

    // 全角文字の場合は2を、半角文字の場合は1を加算
    currentCount += isFullWidth ? 2 : 1;

    // 最大文字数を超えた場合、現在までのテキストを描画して新しい行を開始
    if (currentCount > wordCount) {
      await drawText(page, font, lineText, positionX);
      lineText = char; // 新しい行の最初の文字を設定
      currentCount = isFullWidth ? 2 : 1; // 幅のカウントをリセット
    } else {
      // 最大文字数を超えていない場合は、現在の行に文字を追加
      lineText += char;
    }
  }
  // 最後の行のテキストを描画
  if (lineText) {
    await drawText(page, font, lineText, positionX);
  }
};

/**
 * 文字を描画しポイントを1行ずらす
 *
 * @param page object
 * @param font object
 * @param text 描画文字列
 * @param positionX x座標
 */
const drawText = async (
  page: PDFPage,
  font: PDFFont,
  text: string,
  positionX: number
) => {
  page.drawText(text, {
    size: 10,
    x: positionX,
    y: point,
    font: font
  });
  point = point - 12;
};

/**
 * 下線を引く
 *
 * @param page object
 * @param startX 開始のX座標
 * @param endX 終了のX座標
 */
const drawBar = async (page: PDFPage, startX: number, endX: number) => {
  page.drawLine({
    start: { x: startX, y: point + 11 },
    end: { x: endX, y: point + 11 },
    thickness: 1,
    color: lineColor
  });
};

// メタデータを削除するための関数（画像をHTMLのCanvasに描画し、再エンコードすることでメタデータを削除）
const removeImageMetadata = async (
  imageBytes: ArrayBuffer,
  imageFormat: string
): Promise<Uint8Array> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvasの初期化に失敗しました');

  const img = new Image();
  img.src = URL.createObjectURL(
    new Blob([imageBytes], { type: `image/${imageFormat}` })
  );
  await new Promise(resolve => (img.onload = resolve));

  // 画像の寸法をCanvasに設定して再描画
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  // 再エンコードされた画像データを取得
  const newImageDataUrl = canvas.toDataURL(`image/${imageFormat}`);
  const newImageBytes = await fetch(newImageDataUrl).then(res =>
    res.arrayBuffer()
  );

  return new Uint8Array(newImageBytes);
};

/**
 * 画像のURLから、PDFに表示する画像に変換する
 *
 * @param imageURL Base64形式の文字列
 * @param pdfDoc PDFDocument
 * @returns
 */
const convertBase64ToPDFImage = async (
  imageURL: string,
  pdfDoc: PDFDocument
) => {
  const imageBytes = await fetch(imageURL).then(res => res.arrayBuffer());

  // 画像の形式を取得
  const imageFormat = imageURL.split(';')[0].split('/')[1];

  const sanitizedImageBytes = await removeImageMetadata(
    imageBytes,
    imageFormat
  );

  switch (imageFormat) {
    case 'png':
      return await pdfDoc.embedPng(sanitizedImageBytes);
    case 'jpeg':
    case 'jpg':
      return await pdfDoc.embedJpg(sanitizedImageBytes);
    default:
      throw new Error('PDFに表示できる画像の拡張子は、PNG・JPEG・JPGのみです');
  }
};

/**
 * PDFに表示するときの画像の縦横幅を取得
 *
 * @param image
 * @returns
 */
const getScaledImageShape = (image: PDFImage) => {
  // 画像の幅と高さ
  const width = image.width;
  const height = image.height;

  // ターゲットの幅と高さ
  const targetWidth = 305;
  const targetHeight = 234;

  // 幅と高さを計算
  const widthScale = targetWidth / image.width;
  const heightScale = targetHeight / image.height;

  // 縦横比を維持したまま、より小さい方のスケールを適用する
  const scale = Math.min(widthScale, heightScale);

  // スケーリング後の幅と高さを計算
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  // 画像をスケールし、位置を設定する
  return {
    width: scaledWidth,
    height: scaledHeight
  };
};
